/**
 * 로컬 전용 연결(웹) — **이 브라우저에만, 암호문으로** 둔다(2026-10-07 사용자 요구: 「고객정보·기밀 DB 는 사용자 로컬에만
 * 연결정보가 유지되게」).
 *
 * ## 저장
 *
 * IndexedDB 에 연결 정보(비밀번호 포함)를 **AES-GCM 암호문**으로 둔다. 키는 브라우저가 만든 **내보낼 수 없는(non-extractable)**
 * WebCrypto 키이고 같은 IndexedDB 에 CryptoKey 그대로 담긴다 — 저장소를 들여다봐도 평문이 없고, 스크립트도 키 값을 꺼낼 수 없다
 * (쓰게만 할 수 있다). 사용자(usrid)마다 키·목록을 가른다. 브라우저 데이터를 지우면 연결도 사라진다 — 그것이 「로컬에만」 이다.
 *
 * ## 서버
 *
 * 웹은 SQL 을 서버가 DB 로 보내므로, 쓰는 동안에는 서버가 접속 정보를 알아야 한다. 서버는 **메모리에만** 둔다
 * (connectionManager.registerLocal — 파일·로그·내보내기에 없음). 여기서 2분마다 「가진 목록(id + 지문)」 을 알리고
 * (`syncLocalConnections`), 서버가 없다고 하는 것만 다시 보낸다 — 비밀번호를 매번 보내지 않는다. 알림이 끊기면 서버가 30분 뒤
 * 지우고, 로그아웃하면 즉시 지운다(`clearServerLocal`).
 */
import axios from 'axios';

const DB_NAME = 'livesql-local';
const DB_VERSION = 1;
const KEYS = 'keys';
const CONNS = 'conns';
const BASE = process.env.REACT_APP_API_BASE_URL || '/sql-developer';
const SYNC_MS = 2 * 60 * 1000;

export const isLocalId = (id) => typeof id === 'string' && /^local_[A-Za-z0-9_-]{6,64}$/.test(id);

/** 이 환경에서 쓸 수 있나 — IndexedDB 와 WebCrypto(보안 컨텍스트: https 또는 localhost)가 있어야 한다 */
export function isSupported() {
    return typeof window !== 'undefined' && !!window.indexedDB && !!(window.crypto && window.crypto.subtle);
}

function openDb() {
    return new Promise((resolve, reject) => {
        const req = window.indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(KEYS)) db.createObjectStore(KEYS);
            if (!db.objectStoreNames.contains(CONNS)) db.createObjectStore(CONNS);
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

function idb(store, mode, fn) {
    return openDb().then(db => new Promise((resolve, reject) => {
        const tx = db.transaction(store, mode);
        const out = fn(tx.objectStore(store));
        // 요청의 결과만 돌려준다 — 없는 키면 undefined. (예전 판은 그때 요청 객체를 돌려줘 「키가 있다」 로 읽혔다)
        tx.oncomplete = () => { db.close(); resolve(out && 'result' in out ? out.result : undefined); };
        tx.onerror = () => { db.close(); reject(tx.error); };
    }));
}

/** 사용자 키 — 없으면 만든다(내보낼 수 없는 AES-GCM 256) */
async function keyFor(usrid) {
    const got = await idb(KEYS, 'readonly', st => st.get(String(usrid)));
    if (got) return got;
    const key = await window.crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
    await idb(KEYS, 'readwrite', st => st.put(key, String(usrid)));
    return key;
}

/** 사용자 목록 — { [id]: { iv, data } } (암호문) */
async function rawList(usrid) {
    return (await idb(CONNS, 'readonly', st => st.get(String(usrid)))) || {};
}

// 쓸 때 만든다 — 모듈을 불러오기만 하는 곳(App·테스트 환경)에 TextEncoder 가 없을 수 있다
const enc = { encode: (s) => new TextEncoder().encode(s) };
const dec = { decode: (b) => new TextDecoder().decode(b) };

async function encrypt(key, obj) {
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const data = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(JSON.stringify(obj)));
    return { iv: Array.from(iv), data: new Uint8Array(data) };
}

async function decrypt(key, box) {
    const plain = await window.crypto.subtle.decrypt({ name: 'AES-GCM', iv: new Uint8Array(box.iv) }, key, box.data);
    return JSON.parse(dec.decode(plain));
}

/** 로컬 연결 전부(복호화) — 못 여는 것은 건너뛴다(다른 키로 담긴 옛 것 등) */
export async function listLocal(usrid) {
    if (!usrid || !isSupported()) return [];
    const key = await keyFor(usrid);
    const raw = await rawList(usrid);
    const out = [];
    for (const [id, box] of Object.entries(raw)) {
        try {
            // eslint-disable-next-line no-await-in-loop -- 몇 개 안 된다
            out.push({ ...(await decrypt(key, box)), id, storage: 'local' });
        } catch (_e) { /* 못 연다 — 목록에서 뺀다 */ }
    }
    return out;
}

function newLocalId() {
    const b = window.crypto.getRandomValues(new Uint8Array(8));
    return `local_${Array.from(b, x => x.toString(16).padStart(2, '0')).join('')}`;
}

/** 저장(새것이면 id 를 붙인다). 돌려주는 것은 저장된 연결(평문 — 화면 메모리용) */
export async function saveLocal(usrid, conn) {
    if (!isSupported()) throw new Error('LOCAL_UNSUPPORTED');
    const key = await keyFor(usrid);
    const id = isLocalId(conn.id) ? conn.id : newLocalId();
    const body = { ...conn, id, storage: 'local' };
    const box = await encrypt(key, body);
    const raw = await rawList(usrid);
    raw[id] = box;
    await idb(CONNS, 'readwrite', st => st.put(raw, String(usrid)));
    return body;
}

export async function removeLocal(usrid, id) {
    if (!isSupported()) return;
    const raw = await rawList(usrid);
    delete raw[id];
    await idb(CONNS, 'readwrite', st => st.put(raw, String(usrid)));
}

/** 내용 지문 — 서버가 「바뀌었나」 를 가린다(비밀번호를 매번 보내지 않으려고). 키 순서와 무관하게 같은 값 */
export async function fingerprint(conn) {
    const canon = JSON.stringify(Object.keys(conn).sort().reduce((o, k) => { o[k] = conn[k]; return o; }, {}));
    const h = await window.crypto.subtle.digest('SHA-256', enc.encode(canon));
    return Array.from(new Uint8Array(h), x => x.toString(16).padStart(2, '0')).join('');
}

/**
 * 서버에 알린다 — 없거나 바뀐 것만 보낸다. 실패해도 던지지 않는다(목록 화면을 막지 않게) — 그 연결을 쓸 때 서버가
 * 「연결을 찾을 수 없다」 로 말한다.
 * @returns {Promise<number>} 다시 보낸 개수
 */
export async function syncLocalConnections(usrid) {
    if (!usrid || !isSupported()) return 0;
    try {
        const list = await listLocal(usrid);
        const items = await Promise.all(list.map(async c => ({ id: c.id, fp: await fingerprint(c), conn: c })));
        const r = await axios.post(`${BASE}/connections/local/sync`, { items: items.map(({ id, fp }) => ({ id, fp })) });
        const missing = new Set((r.data && r.data.missing) || []);
        const send = items.filter(x => missing.has(x.id));
        if (send.length) await axios.post(`${BASE}/connections/local`, { connections: send.map(({ conn, fp }) => ({ conn, fp })) });
        return send.length;
    } catch (_e) {
        return 0;
    }
}

/** 주기 동기화 — 앱이 로그인한 사용자로 한 번 시작한다. 돌려준 함수로 멈춘다 */
export function startLocalSync(usrid) {
    if (!usrid || !isSupported()) return () => {};
    const timer = setInterval(() => { syncLocalConnections(usrid); }, SYNC_MS);
    return () => clearInterval(timer);
}

/** 로그아웃 — 서버 메모리의 내 로컬 연결을 즉시 지운다(브라우저의 암호문은 남는다 — 다음 로그인에 다시 쓴다) */
export async function clearServerLocal() {
    try { await axios.delete(`${BASE}/connections/local`); } catch (_e) { /* 서버가 30분 뒤 스스로 지운다 */ }
}
